import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { MultiSelect } from 'primereact/multiselect';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';

// Assuming calculateOrderPrice function is already defined/imported as per your provided snippet.
const AnalyticsPage = ({ filteredOrders }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [customerAnalytics, setCustomerAnalytics] = useState(null);
  const [dayOfWeekData, setDayOfWeekData] = useState({}); // Updated here
  const [orderTypesByDayData, setOrderTypesByDayData] = useState({});
  const [orderTypeDistributionData, setOrderTypeDistributionData] = useState({});
  const [averageSpentPerDayData, setAverageSpentPerDayData] = useState({});

  useEffect(() => {
    analyzeOrders();
  }, [filteredOrders]);

  const analyzeOrders = () => {
    const customerOrderCounts = {};
    let customerSpending = {};
    let dayOfWeekCounts = Array(7).fill(0); // 0 (Sunday) to 6 (Saturday)
    let totalUSD = 0;
    let totalDeliveryFees = 0;
    let orderTypeCounts = { cakes: 0, cookies: 0, cupcakes: 0, cakePops: 0, generalItems: 0 };

    const totalSpentPerDay = Array(7).fill(0);
    const orderCountsPerDay = Array(7).fill(0);

    filteredOrders.forEach(order => {
      const customerName = order.customer.name;
      customerOrderCounts[customerName] = (customerOrderCounts[customerName] || 0) + 1;

      const orderTotal = calculateOrderPrice(order.orderItems[0]) + (order.deliveryFee ? parseFloat(order.deliveryFee) : 0)
        + (order.additionalFees ? order.additionalFees.reduce((a, b) => a + (b['price'] || 0), 0) : 0)
        - (order.discounts ? order.discounts.reduce((a, b) => a + (b['price'] || 0), 0) : 0);
      totalUSD += orderTotal;

      // Track spending per customer
      customerSpending[customerName] = (customerSpending[customerName] || 0) + orderTotal;

      // Calculate total delivery fees
      totalDeliveryFees += order.deliveryFee ? parseFloat(order.deliveryFee) : 0;

      // Count order types
      const dayOfWeek = new Date(order.dueDate).getDay();

      orderTypeCounts.cakes += order.orderItems[0].cakes?.length || 0;
      orderTypeCounts.cookies += order.orderItems[0].cookies?.length || 0;
      orderTypeCounts.cupcakes += order.orderItems[0].cupcakes?.length || 0;
      orderTypeCounts.cakePops += order.orderItems[0].cakePops?.length || 0;
      orderTypeCounts.generalItems += order.orderItems[0].generalItems?.length || 0;

      // Logic for average spent per day
      totalSpentPerDay[dayOfWeek] += orderTotal;
      orderCountsPerDay[dayOfWeek]++;
      dayOfWeekCounts[dayOfWeek]++;
    });

    // Calculate average order value
    let averageOrderValue = totalUSD / filteredOrders?.length;

    const mostFrequentCustomer = Object.keys(customerOrderCounts).reduce((a, b) => customerOrderCounts[a] > customerOrderCounts[b] ? a : b, '');
    const busiestDayIndex = dayOfWeekCounts.findIndex(count => count === Math.max(...dayOfWeekCounts));
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Calculate customer retention rate
    const calculateCustomerRetentionRate = (orders) => {
      const orderCounts = orders.reduce((acc, order) => {
        acc[order.customer.name] = (acc[order.customer.name] || 0) + 1;
        return acc;
      }, {});
      const repeatCustomers = Object.values(orderCounts).filter(count => count > 1).length;
      const totalCustomers = Object.keys(orderCounts).length;
      return (repeatCustomers / totalCustomers) * 100;
    };

    // Calculate customer retention rate
    const customerRetentionRate = calculateCustomerRetentionRate(filteredOrders);

    // Calculate average spent per day
    const averageSpentPerDay = totalSpentPerDay.map((total, i) => orderCountsPerDay[i] > 0 ? total / orderCountsPerDay[i] : 0);


    // Within the analyzeOrders function, add calculations for totalDiscounts and mostPopularProduct
    let totalDiscounts = 0;
    const productQuantities = { cakes: 0, cookies: 0, cupcakes: 0, cakePops: 0, generalItems: 0 };

    filteredOrders.forEach(order => {
      // Other calculations...

      // Calculate total discounts
      if (order.discounts && order.discounts.length > 0) {
        order.discounts.forEach(discount => {
          totalDiscounts += discount.price ? parseFloat(discount.price) : 0;
        });
      }

      // Calculate product quantities
      Object.keys(order.orderItems[0]).forEach(key => {
        if (Array.isArray(order.orderItems[0][key])) {
          productQuantities[key] += order.orderItems[0][key].length;
        }
      });
    });

    setOrderTypeDistributionData({
      labels: ['Cakes', 'Cookies', 'Cupcakes', 'Cake Pops', 'General Items'],
      datasets: [
        {
          data: Object.values(orderTypeCounts),
          backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
          hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF']
        }
      ]
    });

    setAverageSpentPerDayData({
      labels: days,
      datasets: [{
        label: 'Average Spent per Order Day',
        data: averageSpentPerDay,
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#C9CBCF', '#FF9F40'],
      }]
    });


    setCustomerAnalytics({
      mostFrequentCustomer,
      mostFrequentCustomerTotalOrders: customerOrderCounts[mostFrequentCustomer],
      mostFrequentCustomerTotalSpent: customerSpending[mostFrequentCustomer],
      totalOrders: filteredOrders?.length,
      totalUSD,
      totalDeliveryFees,
      averageOrderValue,
      orderTypeCounts,

      customerRetentionRate,
      totalDiscounts,
    });

    // Update dayOfWeekData state
    setDayOfWeekData({
      labels: days,
      datasets: [
        {
          label: 'Order Count by Day of Week',
          data: dayOfWeekCounts,
          backgroundColor: [
            '#42A5F5',
            '#66BB6A',
            '#FFA726',
            '#26C6DA',
            '#7E57C2',
            '#FFCA28',
            '#EF5350'
          ],
          hoverBackgroundColor: [
            '#64B5F6',
            '#81C784',
            '#FFB74D',
            '#4DD0E1',
            '#9575CD',
            '#FFD54F',
            '#E57373'
          ]
        }
      ]
    });
  };

  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const customerOptions = filteredOrders.map(order => ({
    label: order.customer.name,
    value: order.customer.name,
  })).filter((option, index, self) =>
    index === self.findIndex((t) => t.label === option.label && t.value === option.value)
  ).sort((a, b) => a.label.localeCompare(b.label));



  


  return (
    <>
      <Button label="Analytics" icon="pi pi-chart-bar" onClick={() => setShowDialog(true)} size="small" />
      <Dialog header="Order Analytics" visible={showDialog} style={{ width: '50vw' }} onHide={() => setShowDialog(false)} maximizable>



        {customerAnalytics && (
          <TabView>
            <TabPanel header="Summary">
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', alignItems: 'stretch' }}>
                <Tile title="General">
                  <p>Total Orders: {customerAnalytics.totalOrders}</p>
                  <p>Total USD Value Ordered: ${customerAnalytics.totalUSD.toFixed(2)}</p>
                </Tile>

                <Tile title="Most Frequent">
                  <p>Most Frequent Customer: {customerAnalytics.mostFrequentCustomer}</p>
                  <p>Most Frequent Customer Total Orders: {customerAnalytics.mostFrequentCustomerTotalOrders}</p>
                  <p>Most Frequent Customer Total Spent: ${parseFloat(customerAnalytics.mostFrequentCustomerTotalSpent).toFixed(2)}</p>
                </Tile>

                {/* Additional KPIs Tile */}
                <Tile title="Additional KPIs">
                  <p>Average Order Value: ${parseFloat(customerAnalytics.averageOrderValue).toFixed(2)}</p>
                  <p>Total Delivery Fees: ${parseFloat(customerAnalytics.totalDeliveryFees).toFixed(2)}</p>
                  <p>Total Discounts: ${parseFloat(customerAnalytics.totalDiscounts).toFixed(2)}</p>
                </Tile>

                <Tile title="Customer Retention Rate">
                  <p>{parseFloat(customerAnalytics.customerRetentionRate).toFixed(2)}%</p>
                </Tile>

              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', alignItems: 'stretch' }}>
                {/* Order Type Distribution Tile */}
                <Tile title="Order Type Distribution">

                  <>Cakes: {customerAnalytics.orderTypeCounts.cakes}</> <br />
                  <>Cookies: {customerAnalytics.orderTypeCounts.cookies}</> <br />
                  <>Cupcakes: {customerAnalytics.orderTypeCounts.cupcakes}</> <br />
                  <>Cake Pops: {customerAnalytics.orderTypeCounts.cakePops}</> <br />
                  <Chart type="doughnut" data={orderTypeDistributionData} />
                </Tile>

                {/* Orders by Day of Week Tile */}
                <Tile title="Weekday Metrics">
                  <Tile title="Orders by Day of Week">
                    <Chart type="bar" data={dayOfWeekData} />
                  </Tile>

                  {/* Average Spent Per Day Tile */}
                  <Tile title="Average Spent Per Order Day">
                    <Chart type="bar" data={averageSpentPerDayData} />
                  </Tile>
                </Tile>
              </div>
            </TabPanel>
            <TabPanel header="Customer Analytics">
              <div>
                <MultiSelect
                  value={selectedCustomers}
                  options={customerOptions}
                  onChange={(e) => setSelectedCustomers(e.value)}
                  optionLabel="label"
                  placeholder="Select Customers"
                  filter
                  display="chip"
                  style={{
                    maxWidth: '100%',
                  }}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                  {selectedCustomers.map(customerName => {
                    const customerOrders = filteredOrders.filter(order => order.customer.name === customerName);
                    return <CustomerAnalytics key={customerName} name={customerName} orders={customerOrders} />;
                  })}
                </div>
              </div>
            </TabPanel>
            <TabPanel header="Top Customers (By Amount Spent)">
              <TopCustomers orders={filteredOrders} />
            </TabPanel>
          </TabView>
        )}



      </Dialog>
    </>
  );
};

export default AnalyticsPage;

const calculateCustomerAnalytics = (orders) => {
  let totalSpent = 0;
  orders.forEach(order => {
    totalSpent += calculateOrderPrice(order.orderItems[0]) + (order.deliveryFee ? parseFloat(order.deliveryFee) : 0)
      + (order.additionalFees ? order.additionalFees.reduce((a, b) => a + (b['price'] || 0), 0) : 0)
      - (order.discounts ? order.discounts.reduce((a, b) => a + (b['price'] || 0), 0) : 0);
  });

  return {
    totalOrders: orders.length,
    totalSpent,
  };
};

const Tile = ({ title, children }) => (
  <div style={{ margin: '10px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
    {title && <div style={{ fontsize: '16px', fontWeight: 'bold' }}>{title}</div>}
    {children}
  </div>
);


const TopCustomers = ({ orders }) => {
  const uniqueCustomerNames = [...new Set(orders.map(order => order.customer.name))];
  const initialTopCustomerCount = Math.min(5, uniqueCustomerNames.length);
  const [topCustomerCount, setTopCustomerCount] = useState(initialTopCustomerCount);
  const [customerData, setCustomerData] = useState([]);

  useEffect(() => {
    const customerMetrics = orders.reduce((acc, order) => {
      const name = order.customer.name;
      if (!acc[name]) {
        acc[name] = {
          name,
          totalOrders: 0,
          totalSpent: 0,
          deliveryFees: 0,
          averageOrderValue: 0,
          orderTypes: { cakes: 0, cookies: 0, cupcakes: 0, cakePops: 0, generalItems: 0 }
        };
      }
      const orderTotal = calculateOrderPrice(order.orderItems[0]);
      acc[name].totalOrders += 1;
      acc[name].totalSpent += orderTotal;
      acc[name].deliveryFees += order.deliveryFee ? parseFloat(order.deliveryFee) : 0;

      // Increment order type counts
      Object.keys(acc[name].orderTypes).forEach(type => {
        acc[name].orderTypes[type] += order.orderItems[0][type]?.length || 0;
      });

      return acc;
    }, {});

    Object.values(customerMetrics).forEach(customer => {
      customer.averageOrderValue = customer.totalSpent / customer.totalOrders;
    });

    //const sortedCustomers = Object.values(customerMetrics).sort((a, b) => b.totalSpent - a.totalSpent).slice(0, topCustomerCount);
    const sortedCustomers = Object.values(customerMetrics).sort((a, b) => b.totalSpent - a.totalSpent);
    setCustomerData(sortedCustomers);
  }, [orders, topCustomerCount]);


  const dropdownOptions = Array.from({ length: uniqueCustomerNames.length }, (_, i) => ({ label: `Top ${i + 1}`, value: i + 1 }));

  return (
    <div>
      {/* <Dropdown
        value={topCustomerCount}
        options={dropdownOptions}
        onChange={(e) => setTopCustomerCount(e.value)}
        placeholder="Select Number of Top Customers"
      /> */}
      <DataTable sortMode="multiple" value={customerData} paginator rows={10} rowsPerPageOptions={[10, 25, 50]}>
        <Column sortable field="name" header="Customer Name"></Column>
        <Column sortable field="totalOrders" header="Total Orders"></Column>
        <Column sortable field="totalSpent" header="Total Spent" body={rowData => `$${rowData.totalSpent.toFixed(2)}`}></Column>
        <Column sortable field="averageOrderValue" header="Avg Order Value" body={rowData => `$${rowData.averageOrderValue.toFixed(2)}`}></Column>
        <Column sortable field="deliveryFees" header="Total Delivery Fees" body={rowData => `$${rowData.deliveryFees.toFixed(2)}`}></Column>
        <Column header="Order Types" body={(rowData) => {
          const orderTypes = rowData.orderTypes;
          const orderTypesEntries = Object.entries(orderTypes).filter(([type, count]) => count > 0);
          const orderTypesFormatted = orderTypesEntries.map(([type, count]) => {
            return `${type.charAt(0).toUpperCase() + type.slice(1)}: ${count}`;
          }).join('<br>'); // Join with HTML line break for display in DataTable

          return <span dangerouslySetInnerHTML={{ __html: orderTypesFormatted }} />;
        }}></Column>
      </DataTable>
    </div>
  );
};

const CustomerAnalytics = ({ name, orders }) => {
  const analytics = calculateCustomerAnalytics(orders);

  return (
    <div style={{ margin: '0.25rem', padding: '1rem', border: '1px solid #ccc', borderRadius: '5px' }}>
      <h4>{name}</h4>
      <p>Total Orders: {analytics.totalOrders}</p>
      <p>Total Spent: ${analytics.totalSpent.toFixed(2)}</p>
    </div>
  );
};


const calculateOrderPrice = (orderItems) => {
  let totalPrice = 0;

  // For Cakes
  orderItems.cakes.forEach(cake => {
    // Sum up each tier's price
    cake.tiers.forEach(tier => {
      totalPrice += tier.price ? parseFloat(tier.price) : 0;
      totalPrice += tier.premiumFillingPrice &&
        //make sure premiumFilling type is number if not set to 0
        typeof tier.premiumFillingPrice === 'number'
        ? tier.premiumFillingPrice : 0;
      totalPrice += tier.soakPrice ? parseFloat(tier.soakPrice) : 0;
      totalPrice += tier.extrasPrice ? parseFloat(tier.extrasPrice) : 0;
    });

    // Add topper price if exists
    if (cake.toppers.length) {
      cake.toppers.forEach(topper => {
        totalPrice += topper.price ? parseFloat(topper.price) : 0;
      });
    }

    if (cake.decoratingItems.length) {
      cake.decoratingItems.forEach(decoratingItem => {
        totalPrice += decoratingItem.price ? parseFloat(decoratingItem.price) : 0;
      });
    }
  });

  // For Cookies
  orderItems.cookies.forEach(cookie => {
    totalPrice += ((cookie.quantity ? parseInt(cookie.quantity) : 0) * (cookie.price ? parseFloat(cookie.price) : 0));
  });

  // For Cupcakes
  orderItems.cupcakes.forEach(cupcake => {
    totalPrice += ((cupcake.quantity ? parseInt(cupcake.quantity) : 0) * (cupcake.price ? parseFloat(cupcake.price) : 0));
  });

  // For Cake Pops
  orderItems.cakePops.forEach(cakePop => {
    totalPrice += ((cakePop.quantity ? parseInt(cakePop.quantity) : 0) * (cakePop.price ? parseFloat(cakePop.price) : 0));
  });

  // For General Items
  orderItems.generalItems.forEach(item => {
    totalPrice += ((item.quantity ? parseInt(item.quantity) : 0) * (item.price ? parseFloat(item.price) : 0));
  });

  return totalPrice;
};